<template>
  <div class="internal">
    <transition name="fade">
      <confirmation-modal
        v-if="showCreateConfirmation"
        :confirmation-type="confirmation.type"
        :title="confirmation.title"
        :description="confirmation.description"
        :button-text="confirmation.button"
        @confirm="saveAccountDetails"
        @cancel="closeCreateConfirmation"
      />
    </transition>
    <template v-if="showAccountInfo">
      <textarea
        ref="accountInfoTextArea"
        v-model="accountInfo"
        class="internal__textarea"
      />
      <button
        class="btn"
        @click="copyText"
      >
        COPY
      </button>
      <router-link
        class="mt-2"
        :to="{ name: 'login' }"
      >
        Back to Sign In
      </router-link>
    </template>
    <template v-else-if="showCreateAccountForm">
      <div class="internal__title">
        Create Account
      </div>
      <div v-show="errorMessage">
        <div class="internal__error">
          {{ errorMessage }}
        </div>
      </div>
      <div class="internal-container flex-grid">
        <template v-for="field in accountFields">
          <div
            v-if="!field.hidden"
            :key="field.id"
            class="form-container column--two"
          >
            <div class="form-label-container">
              <label class="form-label">{{ field.label }}</label>
              <div class="form__error">
                {{ inputErrors[field.id] }}
              </div>
            </div>
            <template v-if="field.id === 'maxStepupDevices'">
              <input
                v-model.number="field.input"
                class="form-field"
                type="number"
                min="0"
                :max="maxPostgresNumber"
                @input="limitValue(field, 0, maxPostgresNumber)"
              />
            </template>
            <template v-else-if="field.id === 'facilityCode'">
              <toggle
                :toggle.sync="field.input"
                @toggle-clicked="toggleFacilityCode(field)"
              >
              </toggle>
            </template>
            <template v-else>
              <input
                v-model="field.input"
                class="form-field"
                type="text"
              />
            </template>
          </div>
        </template>
      </div>
      <template v-if="loading">
        <loader :size="50" />
      </template>
      <template v-else>
        <button
          class="btn"
          @click="submitAccountDetails"
        >
          Create Account
        </button>
      </template>
    </template>
    <template v-else>
      <div class="internal-container">
        <input
          id="secret-input"
          v-model="secret"
          placeholder="Password please."
          autofocus
          class="form-field"
          type="text"
          @keyup.enter="submitSecret"
        />
      </div>
    </template>
  </div>
</template>

<script>
import accountQueries from '@/graphql/account.js'
import loader from '@/components/loader.vue'
import toggle from '@/components/toggle.vue'
import REGEX from '@/global/utilities.js'
import ConfirmationModal from '@/components/confirmationModal.vue'

export default {
  name: 'AccountCreation',
  components: {
    loader,
    ConfirmationModal,
    toggle,
  },
  mixins: [accountQueries],
  data() {
    return {
      maxPostgresNumber: 2147483647,
      showCreateAccountForm: false,
      showAccountInfo: false,
      loading: false,
      isPhoneValid: false,
      errorMessage: '',
      accountInfo: {},
      secret: '',
      showCreateConfirmation: false,
      confirmation: {
        type: 'friendly',
        title: 'Create Account?',
        description: '',
        button: 'Yes',
      },
      accountFields: [
        {
          id: 'accountName',
          label: 'Account Name',
          input: '',
          required: true,
          validation: /^(?!.*\s{2})[a-zA-Z0-9\s]{4,50}$/,
          errorMessage:
            'Must be 4-50 characters long and only contain alphanumeric characters and hyphens.',
          hidden: false,
        },
        {
          id: 'subDomain',
          label: 'Subdomain',
          input: '',
          required: true,
          validation: /^[a-zA-Z0-9][a-zA-Z0-9-]{2,48}[a-zA-Z0-9]$/,
          errorMessage:
            'Must be 4-50 characters long and only contain alphanumeric characters and hyphens.',
          hidden: false,
        },
        {
          id: 'companyName',
          input: 'dummyCompanyName',
          hidden: true,
        },
        {
          id: 'website',
          label: 'Website',
          input: '',
          required: false,
          validation: REGEX.url,
          errorMessage: 'Please enter a valid website URL',
          hidden: false,
        },
        {
          id: 'companyLogoAddress',
          input: 'example.com',
          hidden: true,
        },
        {
          id: 'address',
          input: 'dummyAddress',
          hidden: true,
        },
        {
          id: 'contactFirstName',
          label: 'First Name',
          input: '',
          required: true,
          validation: /^\S.{2,249}$/,
          errorMessage: 'Must be 3-250 characters long',
          hidden: false,
        },
        {
          id: 'contactLastName',
          label: 'Last Name',
          input: '',
          required: true,
          validation: /^\S.{2,249}$/,
          errorMessage: 'Must be 3-250 characters long',
          hidden: false,
        },
        {
          id: 'email',
          label: 'Email',
          input: '',
          required: true,
          validation: REGEX.email,
          errorMessage: 'Please enter a valid email address',
          hidden: false,
        },
        {
          id: 'phoneNumber',
          input: '000-000-0000',
          hidden: true,
        },
        {
          id: 'maxStepupDevices',
          label: 'Licence Count',
          input: 1,
          required: true,
          hidden: false,
        },
        {
          id: 'facilityCode',
          label: 'Facility Code, enable for MedixSafe.',
          input: false,
          hidden: false,
        },
      ],
    }
  },
  computed: {
    inputErrors() {
      return this.accountFields.reduce((errors, field) => {
        const {input, validation, id, required, errorMessage} = field
        if (input !== '') {
          if (this.isInputValid(field)) {
            errors[id] = errorMessage
          }
        } else if (required) {
          errors[id] = 'Required'
        }
        return errors
      }, {})
    },
  },
  methods: {
    limitValue(field, min, max) {
      if (field.input < min) {
        field.input = min
      } else if (field.input > max) {
        field.input = max
      }
    },
    handlePhoneValidation(phoneObject) {
      this.isPhoneValid = phoneObject.isValid
    },
    toggleFacilityCode(fieldObject) {
      this.getInputValue('facilityCode')
    },
    isInputValid(field) {
      const {input, validation} = field
      return validation && !validation.test(input)
    },
    submitSecret() {
      this.showCreateAccountForm = true
    },
    submitAccountDetails() {
      if (this.validateFields()) {
        const subdomain = this.getInputValue('subDomain')
        const user = `${this.getInputValue('contactFirstName')} ${this.getInputValue('contactLastName')}`
        const email = this.getInputValue('email')
        const stepUpDevices = this.getInputValue('maxStepupDevices')
        const fc = this.getInputValue('facilityCode')

        const accountSummary = `
          Please <strong>CONFIRM</strong> below information is correct as this cannot be changed. Clicking Yes will immediately send an email to the account below. Password will be shared on the next screen. Proceed?
          <br>
          <br>
          <div style=>
            Subdomain: <strong>${subdomain}</strong> <br>
            User: <strong>${user}</strong> <br>
            E-mail: <strong>${email}</strong> <br>
            Max Step-Up Devices: <strong>${stepUpDevices}</strong> <br>
            MedixSafe: <strong>${fc ? 'Yes' : 'No'}</strong> <br>
          </div>
        `
        this.confirmation.description = accountSummary
        this.showCreateConfirmation = true
      }
    },
    getInputValue(fieldName) {
      const field = this.accountFields.find((key) => {
        return key.id === fieldName
      })

      return field ? field.input : ''
    },
    closeCreateConfirmation() {
      this.showCreateConfirmation = false
    },
    validateFields() {
      if (Object.entries(this.inputErrors).length === 0) {
        return true
      }
      this.errorMessage = 'Please make sure all fields are filled in correctly.'
      return false
    },
    validateFacilityCode(facilityCodeToggle) {
      return facilityCodeToggle ? 1 : -1
    },
    async saveAccountDetails() {
      const _accountDetails = {}
      this.errorMessage = ''
      this.accountFields.forEach((field) => {
        _accountDetails[field.id] = field.input
      })
      _accountDetails.website = _accountDetails.website ? _accountDetails.website : 'example.com'
      if (this.validateFields()) {
        try {
          this.loading = true
          this.closeCreateConfirmation()
          _accountDetails.facilityCode = this.validateFacilityCode(_accountDetails.facilityCode)
          const _result = await this.createAccountMutation(this.secret, _accountDetails)
          this.loading = false
          this.showCreateAccountForm = false
          this.showAccountInfo = true
          this.accountInfo = JSON.stringify(_result)
        } catch (error) {
          this.loading = false
          this.errorMessage = this.handleErrorMessage(error.message)
        }
      }
    },
    handleErrorMessage(message) {
      if (message.includes('Invalid secret')) {
        return 'The secret provided is invalid.'
      }
      return 'Unable to create account, please check all fields are valid.'
    },
    copyText() {
      const _textArea = this.$refs.accountInfoTextArea
      _textArea.select()
      document.execCommand('copy')
      document.getSelection().removeAllRanges()
    },
  },
}
</script>

<style lang="scss" scoped>
.internal {
  @include flex-display();
  @include flex-justify-content(center);
  @include flex-align-items(center);
  @include flex-direction(column);
  background: $off-white;
  width: 100%;
  height: 100vh;
  color: $midnight;
  padding: 1em;
  box-sizing: border-box;
  @media #{$mobile} {
    height: auto;
  }
  &__title {
    color: $black;
    font-size: $title-font;
    text-align: center;
    font-weight: 600;
  }
  &__error {
    margin-top: 1em;
    color: $red;
    font-size: $default-font;
    font-weight: 400;
    margin-bottom: 3em;
  }
  &-container {
    @include flex-display();
    @include flex-justify-content(space-between);
    margin-bottom: 2em;
    width: 60%;
    @media #{$mobile} {
      width: 100%;
    }
  }
  &__textarea {
    width: 30%;
    height: 30%;
    padding: 1em;
    margin: 1em;
  }
}
</style>
